// React
import { useState, useMemo } from 'react'

// Mantine
import { Skeleton, Text, Anchor, Table, UnstyledButton, Modal, Button } from '@mantine/core'

// Icons
import { IconChevronUp, IconChevronDown, IconSelector } from '@tabler/icons-react'

// Hooks
import { useComponentsAvailability } from '@/hooks/useComponentsAvailability'

// UI
import SKU from '@/components/ui/SKU'
import Banner from '@/components/ui/Banner'

const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR').format(num)
}

function SortableHeader({ children, sorted, reversed, onSort, style }) {
    const Icon = sorted ? (reversed ? IconChevronDown : IconChevronUp) : IconSelector

    return (
        <Table.Th style={style}>
            <UnstyledButton
                onClick={onSort}
                className="flex items-center gap-1"
            >
                <span>{children}</span>
                <Icon size={14} className={sorted ? 'text-gray-700' : 'text-gray-400'} />
            </UnstyledButton>
        </Table.Th>
    )
}

/**
 * Composant pour afficher la disponibilité des composants
 * Affiche une bannière verte/orange avec un bouton pour voir le détail dans une modale
 *
 * @param {string} mode - 'available' (stock dispo après réservations) | 'physical' (stock physique brut)
 */
export default function ComponentsAvailabilityChecker({
    locationId,
    lines = [],
    isSimulation = false,
    debounceMs = 500,
    mode = 'available',
    compact = true,
}) {
    const {
        result,
        loading,
        isValidating,
        error,
        getComponentStatus,
        allAvailable,
        getComponentPhysicalStatus,
        allPhysicallyAvailable,
        unmappedVariantIds,
    } = useComponentsAvailability({
        locationId,
        lines,
        isSimulation,
        debounceMs,
    })

    const statusFn = mode === 'physical' ? getComponentPhysicalStatus : getComponentStatus
    const isAllOk = mode === 'physical' ? allPhysicallyAvailable : allAvailable
    const isPhysical = mode === 'physical'

    // Modale
    const [modalOpened, setModalOpened] = useState(false)

    // Tri (par défaut: SKU croissant)
    const [sortBy, setSortBy] = useState('sku')
    const [reverseSortDirection, setReverseSortDirection] = useState(false)

    const setSorting = (field) => {
        const reversed = field === sortBy ? !reverseSortDirection : false
        setReverseSortDirection(reversed)
        setSortBy(field)
    }

    const sortedComponents = useMemo(() => {
        if (!result?.components) return []

        let data = result.components.filter((c) => statusFn(c) !== 'available')

        if (!sortBy) return data

        return data.sort((a, b) => {
            let comparison = 0

            switch (sortBy) {
                case 'sku':
                    comparison = (a.sku || '').localeCompare(b.sku || '')
                    break
                case 'name':
                    comparison = (a.name || '').localeCompare(b.name || '')
                    break
                case 'stock':
                    comparison = a.stock_quantity - b.stock_quantity
                    break
                case 'available':
                    comparison = a.available_quantity - b.available_quantity
                    break
                case 'required':
                    comparison = a.required_quantity - b.required_quantity
                    break
                case 'held':
                    comparison = a.hold_quantity - b.hold_quantity
                    break
                default:
                    return 0
            }

            return reverseSortDirection ? -comparison : comparison
        })
    }, [result?.components, sortBy, reverseSortDirection, statusFn])

    const unavailableCount = result?.components?.filter(
        (c) => statusFn(c) !== 'available'
    ).length || 0

    // Premier chargement sans données
    if (loading && !result) {
        return <Skeleton height={compact ? 75 : 140} radius={16} />
    }

    // Erreur
    if (error && !result) {
        return (
            <Banner compact type="error" title={isPhysical ? 'Stock physique' : 'Disponibilité des composants'}>
                <Text size="xs">
                    {isPhysical ? 'Erreur lors de la vérification du stock physique.' : 'Erreur lors de la vérification de la disponibilité.'}
                </Text>
            </Banner>
        )
    }

    // Pas de résultat
    if (!result) {
        return null
    }

    const getNumberColor = (num) => (num <= 0 ? 'red' : 'green')

    return (
        <>
            <Banner
                compact={compact}
                fixedHeight
                type={isValidating ? 'info' : isAllOk ? 'success' : 'warning'}
                title={
                    isValidating
                        ? 'Vérification en cours...'
                        : isPhysical
                            ? 'Stock physique'
                            : 'Disponibilité des composants'
                }
            >
                {!isValidating && (
                    <div className="flex items-center justify-between gap-3">
                        <span>
                            {isAllOk
                                ? isPhysical
                                    ? 'Stock suffisant pour tous les composants.'
                                    : 'Tous les composants sont disponibles.'
                                : [
                                      unavailableCount > 0 &&
                                          (isPhysical
                                              ? `${unavailableCount} composant(s) avec un stock physique insuffisant.`
                                              : `${unavailableCount} composant(s) indisponible(s).`),
                                      unmappedVariantIds.length > 0 &&
                                          `${unmappedVariantIds.length} variante(s) sans nomenclature : stock non vérifiable.`,
                                  ]
                                      .filter(Boolean)
                                      .join(' ')}
                        </span>

                        {!isAllOk && unavailableCount > 0 && (
                            <Button
                                variant="light"
                                size={compact ? 'compact-sm' : 'sm'}
                                color="orange"
                                onClick={() => setModalOpened(true)}
                                className="shrink-0"
                            >
                                Voir le détail
                            </Button>
                        )}
                    </div>
                )}
            </Banner>

            <Modal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                title={isPhysical ? 'Stock physique des composants' : 'Disponibilité des composants'}
                size="xl"
            >
                <Table striped highlightOnHover>
                    <Table.Thead>
                        <Table.Tr>
                            <SortableHeader
                                sorted={sortBy === 'sku'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('sku')}
                                style={{ width: 1, whiteSpace: 'nowrap' }}
                            >
                                SKU
                            </SortableHeader>
                            <SortableHeader
                                sorted={sortBy === 'name'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('name')}
                            >
                                Nom
                            </SortableHeader>
                            <SortableHeader
                                sorted={sortBy === 'stock'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('stock')}
                                style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'right' }}
                            >
                                Phys.
                            </SortableHeader>
                            <SortableHeader
                                sorted={sortBy === 'held'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('held')}
                                style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'right' }}
                            >
                                Rés.
                            </SortableHeader>
                            <SortableHeader
                                sorted={sortBy === 'required'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('required')}
                                style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'right' }}
                            >
                                Dem.
                            </SortableHeader>
                            <SortableHeader
                                sorted={sortBy === 'available'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('available')}
                                style={{ width: 1, whiteSpace: 'nowrap', textAlign: 'right' }}
                            >
                                Dispo.
                            </SortableHeader>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {sortedComponents.map((component) => (
                            <Table.Tr
                                key={component.id}
                                className="bg-orange-50"
                            >
                                <Table.Td style={{ width: 1, whiteSpace: 'nowrap' }}>
                                    <SKU component={component} size="sm" />
                                </Table.Td>
                                <Table.Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Anchor
                                        href={`/components/${component.id}`}
                                        size="sm"
                                    >
                                        {component.name}
                                    </Anchor>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                    <Text
                                        size="sm"
                                        fw={500}
                                        c={getNumberColor(component.stock_quantity)}
                                    >
                                        {formatNumber(component.stock_quantity)}
                                    </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                    <Text size="sm" fw={500} c="dimmed">
                                        {formatNumber(component.hold_quantity)}
                                    </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                    <Text size="sm" fw={500} c="dimmed">
                                        {formatNumber(component.required_quantity)}
                                    </Text>
                                </Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                    <Text
                                        size="sm"
                                        fw={500}
                                        c={getNumberColor(component.available_quantity)}
                                    >
                                        {formatNumber(component.available_quantity)}
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.Tbody>
                </Table>
            </Modal>
        </>
    )
}
